import { getApiBaseUrl } from '../config/api.config'
import { getTokenFromUrl } from '../utils/auth'

import type { EnvironmentWithChina } from '../App'

/**
 * Next Best Action — Segment Profile API custom trait `nba_card`.
 *
 * GET /public/profileapi/{customerAccountNumber}/next-best-actions
 * Downstream: Segment Profile API (https://www.twilio.com/docs/segment/unify/profile-api)
 * Gateway PR: https://github.com/Suitsupply/plt-suitsupply-apim/pull/12
 *
 * The gateway returns the trait's JSON verbatim, or a literal `[]` when the
 * customer has no trait yet — there is no 404 case to handle.
 *
 * Payload shape changed 2026-09-18 (confirmed against
 * pj-suitsupply-data-lz-prd.nba.customer_cards): `{ headline, actions[] }` ->
 * `{ action, bullets[], skip }`. `skip` is assumed to mean "this customer
 * shouldn't get a card" — not yet confirmed with the data/export team
 * (RP-6063), so it is treated the same as an absent trait for now.
 *
 * `card_id` identifies the suggestion for feedback (RP-6164). It is being
 * added to the trait by the data team, so it is read as optional and comes
 * through as `''` until the trait carries it.
 */

export interface NbaCard {
  cardId: string
  action: string
  bullets: string[]
  skip: boolean
}

export type NbaFeedbackScore = 'good' | 'bad'

export const NBA_FEEDBACK_NOTES_MAX_LENGTH = 1000

export class NbaCardApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
  ) {
    super(message)
    this.name = 'NbaCardApiError'
  }
}

export function getNbaCardUrl(
  accountNumber: string,
  env: EnvironmentWithChina,
): string {
  return `${getApiBaseUrl(env)}/public/profileapi/${encodeURIComponent(
    accountNumber,
  )}/next-best-actions`
}

function buildHeaders(authToken?: string): Record<string, string> {
  const token = authToken || getTokenFromUrl()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  return headers
}

export async function fetchNbaCard(
  accountNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<NbaCard | null> {
  const normalizedAccountNumber = accountNumber.trim()

  if (!normalizedAccountNumber) {
    throw new NbaCardApiError('Missing account number.')
  }

  const url = getNbaCardUrl(normalizedAccountNumber, env)

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: buildHeaders(authToken),
      mode: 'cors',
      credentials: 'omit',
    })

    if (!response.ok) {
      throw new NbaCardApiError(
        `Failed to fetch next best action: ${response.statusText}`,
        response.status,
      )
    }

    const data: unknown = await response.json()

    // The gateway responds with a literal `[]` when the customer has no
    // `nba_card` trait yet — that is the expected "nothing to show" case,
    // not an error.
    if (!data || Array.isArray(data)) {
      return null
    }

    const {
      card_id: cardId,
      action,
      bullets,
      skip,
    } = data as Partial<Omit<NbaCard, 'cardId'>> & { card_id?: unknown }
    if (
      skip === true ||
      typeof action !== 'string' ||
      !action.trim() ||
      !Array.isArray(bullets)
    ) {
      return null
    }

    const validBullets = [
      ...new Set(
        bullets.filter(
          (bullet): bullet is string =>
            typeof bullet === 'string' && bullet.trim().length > 0,
        ),
      ),
    ]

    return {
      cardId: typeof cardId === 'string' ? cardId : '',
      action,
      bullets: validBullets,
      skip: false,
    }
  } catch (error) {
    if (error instanceof NbaCardApiError) {
      throw error
    }

    throw new NbaCardApiError(
      `Network error while fetching next best action: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    )
  }
}
